<template>
    <div class="fulfillment-list-content">
        <section class="horizontal-align">
            <label>{{ $t('funnel.edit.fulfillment.list.title') }}</label>
            <p>{{ $t('funnel.edit.fulfillment.list.summary') }}</p>
        </section>
        <section class="horizontal-center">
            <span>{{ $t('funnel.edit.fulfillment.list.keap') }}</span>
            <span>{{ $t('funnel.edit.fulfillment.list.sheet') }}</span>
        </section>
        <section>
            <Draggable
                :value="fulfillmentOptions.exportFields"
                group="fields"
                handle=".field-icon"
                @end="onDragEnd"
                @input="(exportFields) => $emit('update-fulfillment-options', { ...fulfillmentOptions, exportFields })"
            >
                <FulfillmentExportField
                    v-for="(field, index) in fulfillmentOptions.exportFields"
                    :key="index"
                    :data-qa="`fulfillment-fields-${index}`"
                    :identifier="index"
                    :field="field"
                    :value-array="fulfillmentObject.fieldNames"
                    :label-array="fulfillmentObject.fieldLabels"
                    :category-options="fulfillmentOptions.fieldCategoryOptions"
                    @configure-field="($event) => configureField(index, $event)"
                    @configure-label="($event) => configureLabel(index, $event)"
                    @delete-field="deleteField(index)"
                />
            </Draggable>
        </section>
        <section class="horizontal-center bottom-line">
            <DsTextButton
                data-qa="add-export-field"
                leading-icon="add"
                @click="addExportField"
            >
                {{ $t('funnel.edit.fulfillment.list.add') }}
            </DsTextButton>
        </section>
        <section class="horizontal-align help-text">
            <p>{{ $t('funnel.edit.fulfillment.list.help') }}</p>
            <p>
                {{ $t('funnel.edit.fulfillment.list.help.1') }}
                <a href="https://learn.thryv.com/hc/en-us" target="_blank" rel="noopener noreferrer">
                    {{ $t('funnel.edit.fulfillment.list.help.link') }}
                </a>
                {{ $t('funnel.edit.fulfillment.list.help.2') }}
            </p>
        </section>
    </div>
</template>

<script>
import FulfillmentExportField from '../fulfillmentExportField/FulfillmentExportField.vue';
import Draggable from 'vuedraggable';

export default {
    components: {
        FulfillmentExportField,
        Draggable,
    },

    props: {
        cell: {
            type: Object,
            required: true,
        },
        fulfillmentObject: {
            type: Object,
            required: true,
        },
        fulfillmentOptions: {
            type: Object,
            required: true,
        },
    },
    emits: ['update-fulfillment-object', 'update-fulfillment-options'],

    methods: {
        deleteField(index) {
            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                fieldNames: this.fulfillmentObject.fieldNames.toSpliced(index, 1),
                fieldLabels: this.fulfillmentObject.fieldLabels.toSpliced(index, 1),
            });
            this.$emit('update-fulfillment-options', {
                ...this.fulfillmentOptions,
                exportFields: this.fulfillmentOptions.exportFields.toSpliced(index, 1),
            });
        },

        configureField(index, value) {
            const field = this.findFieldFromOptions(value);

            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                fieldNames: this.fulfillmentObject.fieldNames.toSpliced(index, 1, field.name),
                fieldLabels: this.fulfillmentObject.fieldLabels.toSpliced(index, 1, field.label),
            });
            this.$emit('update-fulfillment-options', {
                ...this.fulfillmentOptions,
                exportFields: this.fulfillmentOptions.exportFields.toSpliced(index, 1, { name: field.name, label: field.label }),
            });
        },

        configureLabel(index, label) {
            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                fieldLabels: this.fulfillmentObject.fieldLabels.toSpliced(index, 1, label),
            });
            this.$emit('update-fulfillment-options', {
                ...this.fulfillmentOptions,
                exportFields: this.fulfillmentOptions.exportFields.toSpliced(index, 1, {
                    ...this.fulfillmentOptions.exportFields[index],
                    label,
                }),
            });
        },

        addExportField() {
            this.$emit('update-fulfillment-options', {
                ...this.fulfillmentOptions,
                exportFields: [...this.fulfillmentOptions.exportFields, { label: '', name: '' }],
            });
            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                fieldNames: [...this.fulfillmentObject.fieldNames, ''],
                fieldLabels: [...this.fulfillmentObject.fieldLabels, ''],
            });
        },

        findFieldFromOptions(value) {
            return this.fulfillmentOptions.exportFieldsOptions.find((entry) => entry.name === value) || { name: '', label: '' };
        },

        onDragEnd() {
            this.updateCollections(this.fulfillmentOptions.exportFields);
        },

        updateCollections(items) {
            this.$emit('update-fulfillment-object', {
                ...this.fulfillmentObject,
                fieldNames: items.map((item) => item.name),
                fieldLabels: items.map((item) => item.label),
            });
        },
    },
};
</script>
<style lang="scss" scoped>
.fulfillment-list-content {
    max-width: px-to-rem(592);

    section.horizontal-align {
        padding-bottom: $spacing-300;
    }

    label {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 auto;
        font-size: $font-size-jumbo;
        font-weight: 400;
        line-height: $line-height-xxl;
        padding-bottom: $spacing-100;
    }
    p {
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        margin: 0 auto;
        font-size: $font-size-md;
        font-weight: 400;
        line-height: $line-height-lg;
    }

    .horizontal-center {
        width: 100%;
        display: flex;
        justify-content: center;

        span {
            width: 50%;
            text-align: center;
            padding-bottom: $spacing-200;
            font-size: $font-size-xs;
            font-weight: 500;
            line-height: $line-height-sm;
        }
    }

    .bottom-line {
        border-bottom: 1px solid $color-gray-100;
        padding-bottom: $spacing-300;
    }

    .help-text {
        padding-top: $spacing-300;
        font-size: $font-size-sm;
        font-weight: 400;
        line-height: $line-height-md;

        a {
            color: $color-blue-700;
            padding-left: 4px;
            padding-right: 4px;
        }
    }
}
</style>
